import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import MainContext from "../../../context/mainContext";
import { useNavigate, useParams } from "react-router";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  IconButton,
  Paper,
} from "@mui/material";
import styles from "./Teams.module.scss";
import AdminHeader from "../AdminHeader";
import { PaginatedDataTable } from "../../../components/Table";
import teamService from "../../../services/teams.service";
import { getLabel } from "../../../misc/utils.functions";
import { GridColDef } from "@mui/x-data-grid";
import { ITeamMemberModel, ITeamModel } from "../../../models/team.model";
import { PagedFilter } from "../../../misc/pagedFilter";
import teamsService from "../../../services/teams.service";
import { PeoplePicker } from "../../../components/SelectPicker/PeoplePickers";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import { IUser } from "../../../models/user.model";
import { RemoveIcon } from "../../../assets/icons";
import BackButton from "../../../components/BackButton";
import AdminMenu from "../AdminMenu";

const TeamEdit: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { code } = useParams();
  const { service, currentUser } = useContext(MainContext);

  const initFilter: PagedFilter = {
    pageNumber: 1,
    pageSize: 5,
    sortField: "firstName",
    sortDescending: false,
  };

  const [currentFilter, setCurrentFilter] = useState<PagedFilter>(initFilter);
  const [team, setTeam] = useState<ITeamModel | undefined>();
  const [members, setTeamMembers] = useState<ITeamMemberModel[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [newMember, setNewMember] = useState<IUser | null>(null);
  const [columns, setColumns] = useState<GridColDef[]>([]);

  const removeMember = (code: string, userId: number) => {
    service(() => teamsService.removeMember(code, userId), {
      callback: () => {
        setCurrentFilter({ ...initFilter });
      },
    });
  };

  const isMembersEditable = (code: string) => {
    const notMemberEditables = [
      "STAKEHOLDER",
      "MAIN_DIVISION",
      "PEER_REVIEWERS",
    ];

    return notMemberEditables.indexOf(code) === -1;
  };

  const setCoordinator = (code: string, userId: number) => {
    service(() => teamsService.setCoordinator(code, userId), {
      callback: () => {
        setCurrentFilter({ ...initFilter });
      },
    });
  };

  const baseColumns: GridColDef[] = [
    {
      field: "firstName",
      headerName: getLabel("firstName"),
      sortable: true,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "lastName",
      headerName: getLabel("lastName"),
      sortable: true,
      flex: 1,
      minWidth: 150,
    },
  ];

  useEffect(() => {
    if (code !== undefined) {
      let calculated = [...baseColumns];

      if (code === "LEGALES") {
        calculated.push({
          field: "isCoordinator",
          headerName: getLabel("coordinator"),
          sortable: false,
          flex: 1,
          minWidth: 100,
          renderCell: (params) => {
            return (
              <Checkbox
                checked={params.row.isCoordinator}
                onChange={(event: ChangeEvent<any>, checked: boolean) => {
                  if (checked) {
                    setCoordinator(code, params.row.id);
                  }
                }}
              />
            );
          },
        });
      }

      calculated.push({
        field: "actions",
        headerName: getLabel("actions"),
        headerClassName: "actions",
        sortable: false,
        cellClassName: "actions",
        width: 100,
        renderCell: (params) => {
          return params.row.isCoordinator ||
            params.row.id === currentUser?.id ? (
            <></>
          ) : (
            <div>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  removeMember(code, params.row.id);
                }}
              >
                <RemoveIcon />
              </IconButton>
            </div>
          );
        },
      });

      setColumns(calculated);
    }
  }, [code]);

  const handlePageChange = (pageNumber: number) => {
    let filter = { ...currentFilter, pageNumber: pageNumber };
    setCurrentFilter(filter);
  };

  const handleSortChange = (field: string, sortDescending: boolean) => {
    let filter = {
      pageNumber: 1,
      pageSize: 5,
      sortField: field,
      sortDescending: sortDescending,
    };
    setCurrentFilter(filter);
  };

  useEffect(() => {
    if (code !== undefined) {
      service(() => teamsService.getTeam(code), {
        callback: (team) => setTeam(team),
        handleError: (error: any) => {
          if ("response" in error && error.response.status === 403) {
            navigate("/unauthorized");
          }
        },
      });
    }
  }, [code, service]);

  useEffect(() => {
    if (code !== undefined) {
      setLoading(true);

      service(() => teamService.getTeamMembers(code, currentFilter), {
        callback: (result) => {
          setTeamMembers(result.list);
          setTotalRows(result.totalRows);
          setLoading(false);
        },
        handleError: (error: any) => {
          if ("response" in error && error.response.status === 403) {
            navigate("/unauthorized");
          }
        },
      });
    }
  }, [code, currentFilter]);

  const onClickAddMember = () => {
    if (newMember != null) {
      service(() => teamsService.addMember(team!.code, newMember), {
        callback: () => {
          setNewMember(null);
          setCurrentFilter(initFilter);
        },
      });
    }
  };

  return (
    <>
      <div className={styles.adminLayout}>
        <div className={styles.menu}>
          <AdminMenu />
        </div>
        <div className={styles.content}>
          <AdminHeader text={team?.description || ""} />
          {isMembersEditable(code ?? "") ? (
            <Box className={styles.main}>
              <Paper elevation={3} className={styles.basic}>
                <BackButton text="back" />
                <div className={styles.memberRow}>
                  <FormControl fullWidth className={styles.control}>
                    <FormLabel id="member-label">{t("addNewMember")}</FormLabel>
                    <PeoplePicker
                      id="member"
                      defaultValue={newMember}
                      readOnly={false}
                      placeholder={t("member").toString()}
                      onChange={(newMember) => setNewMember(newMember)}
                      onBlur={() => {}}
                    />
                  </FormControl>
                  <Box>
                    <Button
                      onClick={() => onClickAddMember()}
                      className={styles.addMember}
                    >
                      <AddCircleOutlineRoundedIcon />
                      {t("addMember")}
                    </Button>
                  </Box>
                </div>
                <div className={styles.header}>
                  <h3>{t("members")}</h3>
                </div>
                <PaginatedDataTable<ITeamModel>
                  rows={members}
                  totalRows={totalRows}
                  initialPage={currentFilter.pageNumber - 1}
                  pageSize={currentFilter.pageSize}
                  columns={columns}
                  handlePageChange={handlePageChange}
                  handleSortChange={handleSortChange}
                  emptyMessage={t("noRowsMessage", {
                    entity: t("members"),
                  })}
                  loading={loading}
                />
              </Paper>
            </Box>
          ) : (
            <></>
          )}
        </div>
      </div>
    </>
  );
};

export default TeamEdit;
